import {SalesInsightProps, RevenueItem} from "./sales-insight.types";

/**
 * SalesInsight entity - deep module for live event revenue and attendee metrics.
 *
 * Owns data AND rules: the UI calls these methods instead of doing revenue logic itself.
 * Pure TypeScript - no React imports.
 */
export class SalesInsight {
  private readonly props: SalesInsightProps;

  private constructor(props: SalesInsightProps) {
    this.props = props;
  }

  /**
   * Creates a SalesInsight entity with validation.
   * @throws Error if totalAttendees is negative
   */
  static create(props: Partial<SalesInsightProps> = {}): SalesInsight {
    const totalAttendees = props.totalAttendees ?? 0;
    const revenueByCurrency = props.revenueByCurrency ?? {};

    if (totalAttendees < 0) {
      throw new Error("totalAttendees cannot be negative");
    }

    return new SalesInsight({totalAttendees, revenueByCurrency});
  }

  get totalAttendees(): number {
    return this.props.totalAttendees;
  }

  get revenueByCurrency(): Record<string, number> {
    return {...this.props.revenueByCurrency};
  }

  /**
   * Returns all currencies with revenue data.
   */
  getCurrencies(): string[] {
    return Object.keys(this.props.revenueByCurrency);
  }

  /**
   * Returns true if there are 2+ currencies with revenue.
   */
  hasMultipleCurrencies(): boolean {
    return this.getCurrencies().length > 1;
  }

  /**
   * Returns the revenue amount for a specific currency.
   */
  getRevenueFor(currency: string): number {
    return this.props.revenueByCurrency[currency] ?? 0;
  }

  /**
   * Returns the first (primary) revenue entry, or null if no revenue.
   * Used for the single-revenue card display.
   */
  getPrimaryRevenue(): RevenueItem | null {
    const currencies = this.getCurrencies();
    if (currencies.length === 0) {
      return null;
    }
    const currency = currencies[0];
    return {currency, amount: this.props.revenueByCurrency[currency]};
  }

  /**
   * Returns all revenue entries in insertion order.
   * Used for the multi-currency breakdown modal.
   */
  getRevenueBreakdown(): RevenueItem[] {
    return Object.entries(this.props.revenueByCurrency).map(([currency, amount]) => ({
      currency,
      amount,
    }));
  }
}
